import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { useCurrentUserRoles } from 'src/hooks/services/useUser';
import { getLineItemModel } from 'src/utils/lineItem';

import WorkInstructions from './_work-instructions';


const PrintsGroupWithWorkInstructions = ({
  user,
  printsGroup,
  modelsByUri,
  lineItemsByUri,
  checklistsByPrintUri,
  reportsByPrintUri,
  uploadingStateByPrintUri,
  handleStepInput,
  handleStepChange,
  sendNCReviewForPiece,
  runTransformation,
  prints,
  sentToNCRItems,
  setSentToNCRItems,
  savedReportsByPrintUri,
  hasNCRNotSentOutsideOfThreshold,
  setIsAbleToCompleteRun,
  isFileUploading,
  isPowderWorkflow,
  reportFilesPrintURI,
  documentState,
  documentsByReportDataUri,
  handleActiveInstructionChange,
  isInstructionSubmitting,
}) => {
  // Using first print as the base one to render group
  const firstPrint = printsGroup.prints[0];
  const checklist = checklistsByPrintUri[firstPrint.uri];

  const reportFilesByPrint = reportFilesPrintURI[firstPrint.uri] || {};
  const { currentDocumentUri } = documentState;

  const lineItem = lineItemsByUri[firstPrint.line_item];
  const model = (lineItem && !isPowderWorkflow) ? modelsByUri[getLineItemModel(lineItem)] : null;
  const reportValues = reportsByPrintUri[firstPrint.uri];
  const savedReportValues = savedReportsByPrintUri[firstPrint.uri];
  const uploadingState = uploadingStateByPrintUri[firstPrint.uri];
  const hasIncorrectValues = hasNCRNotSentOutsideOfThreshold(checklist?.work_instructions);

  const { data: userRoles } = useCurrentUserRoles(user);
  const isServiceProvider = userRoles?.includes('service-provider');

  const [documentParserAvailable, setDocumentParserAvailable] = useState(false);

  const onStepInput = (workInstructionUUID, value) => {
    handleStepInput(
      printsGroup.prints.map((print) => print.uri),
      workInstructionUUID,
      value,
    );
  };
  const onStepChange = (previousStepIndex) => {
    // TODO: fix steps synchronization between collapsed/expanded states
    handleStepChange(printsGroup.prints.map((print) => print.uri), previousStepIndex);
  };

  const renderExtractReportNotification = useCallback(() => {
    if (printsGroup.collapsed && currentDocumentUri && documentParserAvailable) {
      return (
        <span className="text-muted">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
          Expand Instruction Group to View Extracted Reports
        </span>
    )}

    return null;
  }, [printsGroup.collapsed, currentDocumentUri, documentParserAvailable]);

  return (
    <div key={printsGroup.groupKey} className="mb-4">
      <div className={`${!model && 'justify-content-center'} row no-gutters work-instructions-body`}>
        {model && (
          <div className="col-5 work-instructions-body-model">
            <div className="thumbnail-box rounded-sm">
              <div className="thumbnail-box-inside">
                {
                  // TODO: Show prints count badge
                }
                <img src={model.snapshot_content} alt="" className="img-fluid" />
              </div>
            </div>
            {!isServiceProvider && renderExtractReportNotification()}
          </div>
        )}

        <div className="col-7 work-instructions-body-content work-instructions-body-content-text">
          {checklist && (
            <WorkInstructions
              instructions={checklist.work_instructions}
              values={reportValues}
              uploading={uploadingState}
              beforeChange={onStepChange}
              singleGroup={printsGroup}
              sendNCReviewForPiece={sendNCReviewForPiece}
              print={firstPrint}
              runTransformation={runTransformation}
              allPrints={prints}
              sentToNCRItems={sentToNCRItems}
              setSentToNCRItems={setSentToNCRItems}
              savedReportsByPrintUri={savedReportsByPrintUri}
              hasIncorrectValues={hasIncorrectValues}
              savedValues={savedReportValues}
              setIsAbleToCompleteRun={setIsAbleToCompleteRun}
              reportFilesByPrint={reportFilesByPrint}
              isFileUploading={isFileUploading}
              handleActiveInstructionChange={handleActiveInstructionChange}
              documentsByReportDataUri={documentsByReportDataUri}
              setDocumentParserAvailable={setDocumentParserAvailable}
              isServiceProvider={isServiceProvider}
              renderExtractReportNotification={renderExtractReportNotification}
              isInstructionSubmitting={isInstructionSubmitting}
              onStepInput={onStepInput}
            />
          )}
          {!checklist && (
            <div>No Work Instructions for Pieces</div>
          )}
        </div>
      </div>
    </div>
  );
};

PrintsGroupWithWorkInstructions.propTypes = {
  printsGroup: PropTypes.shape({
    groupKey: PropTypes.string.isRequired,
    prints: PropTypes.arrayOf(
      PropTypes.shape({
        uri: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        // eslint-disable-next-line camelcase
        line_item: PropTypes.string.isRequired,
      }).isRequired,
    ).isRequired,
    collapsed: PropTypes.bool.isRequired,
  }).isRequired,
  modelsByUri: PropTypes.shape({}).isRequired,
  lineItemsByUri: PropTypes.shape({}).isRequired,
  checklistsByPrintUri: PropTypes.shape({}).isRequired,
  reportsByPrintUri: PropTypes.shape({}).isRequired,
  uploadingStateByPrintUri: PropTypes.shape({}).isRequired,
  handleStepInput: PropTypes.func.isRequired,
  handleStepChange: PropTypes.func.isRequired,
  sendNCReviewForPiece: PropTypes.func.isRequired,
  runTransformation: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  prints: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  sentToNCRItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSentToNCRItems: PropTypes.func.isRequired,
  savedReportsByPrintUri: PropTypes.shape({}).isRequired,
  hasNCRNotSentOutsideOfThreshold: PropTypes.func.isRequired,
  setIsAbleToCompleteRun: PropTypes.func.isRequired,
  isFileUploading: PropTypes.bool.isRequired,
  isPowderWorkflow: PropTypes.bool.isRequired,
  reportFilesPrintURI: PropTypes.shape({}).isRequired,
  documentsByReportDataUri: PropTypes.shape({}),
  documentState: PropTypes.shape({
    currentDocumentUri: PropTypes.string,
  }),
  handleActiveInstructionChange: PropTypes.func,
  user: PropTypes.shape({}).isRequired,
  isInstructionSubmitting: PropTypes.bool.isRequired,
};

PrintsGroupWithWorkInstructions.defaultProps = {
  documentState: { currentDocumentUri: null },
  documentsByReportDataUri: {},
  handleActiveInstructionChange: null,
};

export default PrintsGroupWithWorkInstructions;
